<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import ContactAPI from 'dashboard/api/contacts';

const props = defineProps({
  contact: { type: Object, required: true },
  contactId: { type: Number, required: true },
});

const store = useStore();
const getters = useStoreGetters();

// ── Tabs ──────────────────────────────────────────────────────────────────────
const activeTab = ref('dados');

// ── Form state ────────────────────────────────────────────────────────────────
const formData = ref({});
const skyData = ref({});
const isDirty = ref(false);
const isSaving = ref(false);

// Collapsible sections state
const openSections = ref({ entregaveis: false, integracoes: false, negociacao: false, documentos: false, link_adicionais: false, transcricoes: false });

// ── IBGE ──────────────────────────────────────────────────────────────────────
const cities = ref([]);
const loadingCities = ref(false);

async function loadCities(uf) {
  if (!uf) { cities.value = []; return; }
  loadingCities.value = true;
  try {
    const res = await fetch(
      `https://servicodados.ibge.gov.br/api/v1/localidades/estados/${uf}/municipios?orderBy=nome`
    );
    const data = await res.json();
    cities.value = data.map(c => c.nome);
  } catch {
    cities.value = [];
  } finally {
    loadingCities.value = false;
  }
}

watch(() => formData.value.estado, uf => loadCities(uf));

// ── Computed ──────────────────────────────────────────────────────────────────
const totalValue = computed(() => {
  const impl = parseFloat(formData.value.valor_implementacao) || 0;
  const mens = parseFloat(formData.value.valor_mensalidade) || 0;
  return impl + mens * 12;
});

const totalValueFormatted = computed(() =>
  totalValue.value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
);

const termometroConfig = computed(() => {
  const map = {
    frio: { icon: 'i-lucide-thermometer-snowflake', color: 'text-blue-500', bg: 'bg-blue-50 dark:bg-blue-900/20' },
    morno: { icon: 'i-lucide-thermometer', color: 'text-amber-500', bg: 'bg-amber-50 dark:bg-amber-900/20' },
    quente: { icon: 'i-lucide-flame', color: 'text-red-500', bg: 'bg-red-50 dark:bg-red-900/20' },
  };
  return map[formData.value.termometro] || { icon: 'i-lucide-minus', color: 'text-n-slate-8', bg: '' };
});

const statusColors = {
  'venda-ganha': 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  'implementado': 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  'venda-perdida': 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  'aguardando': 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  'reuniao-diagnostico': 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  'reuniao-projeto': 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
};
const statusColor = computed(() => statusColors[formData.value.sky_status] || 'bg-n-slate-2 text-n-slate-10');

// ── Sync from contact prop ────────────────────────────────────────────────────
function syncFromContact() {
  // getContactById returns camelCase keys; getContact returns snake_case — support both
  const customAttrs = props.contact?.custom_attributes ?? props.contact?.customAttributes ?? {};
  formData.value = { ...customAttrs };

  const additionalAttrs = props.contact?.additional_attributes ?? props.contact?.additionalAttributes ?? {};
  const rawSkyData = additionalAttrs?.sky_data ?? additionalAttrs?.skyData;
  if (rawSkyData) {
    try {
      skyData.value = typeof rawSkyData === 'string' ? JSON.parse(rawSkyData) : { ...rawSkyData };
    } catch {
      skyData.value = {};
    }
  } else {
    skyData.value = {};
  }
  if (formData.value.estado) loadCities(formData.value.estado);
}

watch(() => props.contact, syncFromContact, { deep: true });
onMounted(syncFromContact);

// ── Save ──────────────────────────────────────────────────────────────────────
async function save() {
  if (isSaving.value) return;
  isSaving.value = true;
  try {
    const existingAdditional = props.contact?.additional_attributes ?? props.contact?.additionalAttributes ?? {};
    const response = await ContactAPI.update(props.contactId, {
      custom_attributes: { ...formData.value },
      additional_attributes: {
        ...existingAdditional,
        sky_data: JSON.stringify(skyData.value),
      },
    });
    // Update store with fresh data
    store.dispatch('contacts/show', { id: props.contactId });
    isDirty.value = false;
    useAlert('Informações salvas com sucesso!');
  } catch (e) {
    const msg = e?.response?.data?.message || 'Erro ao salvar. Tente novamente.';
    useAlert(msg);
  } finally {
    isSaving.value = false;
  }
}

// ── Dynamic list helpers ──────────────────────────────────────────────────────
function addItem(listKey, template) {
  if (!skyData.value[listKey]) skyData.value[listKey] = [];
  skyData.value[listKey].push({ ...template, id: Date.now() });
  isDirty.value = true;
}

function removeItem(listKey, index) {
  skyData.value[listKey].splice(index, 1);
  isDirty.value = true;
}

function markDirty() { isDirty.value = true; }

// ── Negociação templates ──────────────────────────────────────────────────────
const NEG_IMPL_TEMPLATE = `Valor total da implementação:
Quantidade de parcelas:
Valor por parcela:
Valor por entregável:
Prazo de implementação:
Condições comerciais adicionais: `;

const NEG_MENS_TEMPLATE = `Valor mensal:
Limite de uso incluso:
Valor adicional por excedente:
Modelo de cobrança:
Início da cobrança:
Regras de reajuste/renovação: `;

function initNegTemplate(field, template) {
  if (!formData.value[field]) {
    formData.value[field] = template;
    isDirty.value = true;
  }
}

// ── Status labels ─────────────────────────────────────────────────────────────
const STATUS_OPTIONS = [
  'caixa-entrada', 'qualificacao-ia', 'aguardando', 'consultor', 'atendimento',
  'agendado', 'reuniao-diagnostico', 'reuniao-projeto', 'down-sell', 'follow-up',
  'venda-ganha', 'venda-perdida', 'novo-cliente', 'call-boas-vindas', 'assinar-contrato',
  'entrevista-tecnica', 'preparacao-treinamento', 'montar-fluxos-integracoes',
  'treinamento-ia', 'montar-agente-inciail', 'testes-iniciais',
  'pre-implementacao', 'implementado', 'ajustes',
];

const BRAZIL_STATES = [
  'AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG',
  'PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO',
];
</script>

<template>
  <div class="flex flex-col gap-0">

    <!-- ── Header com tabs ─────────────────────────────────────────────────── -->
    <div class="flex items-center justify-between px-3 pt-2 pb-0 border-b border-n-weak">
      <div class="flex gap-0">
        <button
          class="px-4 py-2 text-xs font-semibold border-b-2 transition-colors"
          :class="activeTab === 'dados'
            ? 'border-n-brand text-n-brand'
            : 'border-transparent text-n-slate-9 hover:text-n-slate-11'"
          @click="activeTab = 'dados'"
        >
          <span class="i-lucide-user mr-1 inline-block size-3.5 align-middle" />
          Dados
        </button>
        <button
          class="px-4 py-2 text-xs font-semibold border-b-2 transition-colors"
          :class="activeTab === 'projeto'
            ? 'border-n-brand text-n-brand'
            : 'border-transparent text-n-slate-9 hover:text-n-slate-11'"
          @click="activeTab = 'projeto'"
        >
          <span class="i-lucide-briefcase mr-1 inline-block size-3.5 align-middle" />
          Projeto
        </button>
      </div>

    </div>

    <!-- ── ABA DADOS ────────────────────────────────────────────────────────── -->
    <div v-if="activeTab === 'dados'" class="flex flex-col gap-0 text-sm">

      <!-- Termômetro + Status (topo visual) -->
      <div class="flex items-center gap-2 px-3 py-2 bg-n-slate-1 dark:bg-n-surface-2 border-b border-n-weak">
        <div
          class="flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium"
          :class="termometroConfig.bg"
        >
          <span :class="[termometroConfig.icon, termometroConfig.color, 'size-3.5']" />
          <span :class="termometroConfig.color">{{ formData.termometro || '—' }}</span>
        </div>
        <span
          v-if="formData.sky_status"
          class="px-2 py-0.5 rounded-full text-xs font-medium"
          :class="statusColor"
        >
          {{ formData.sky_status }}
        </span>
        <span v-else class="text-xs text-n-slate-8">Sem status</span>
      </div>

      <!-- Seção: Identificação -->
      <div class="px-3 py-2 border-b border-n-weak">
        <p class="text-xs font-semibold text-n-slate-8 uppercase tracking-wider mb-2">Identificação</p>
        <div class="grid grid-cols-2 gap-2">
          <div>
            <label class="block text-xs text-n-slate-9 mb-0.5">Nome do Cliente</label>
            <input v-model="formData.clientName" type="text" placeholder="Nome completo"
              class="sky-input" @input="markDirty" />
          </div>
          <div>
            <label class="block text-xs text-n-slate-9 mb-0.5">Nome da Empresa</label>
            <input v-model="formData.nome_empresa" type="text" placeholder="Empresa"
              class="sky-input" @input="markDirty" />
          </div>
          <div>
            <label class="block text-xs text-n-slate-9 mb-0.5">CNPJ</label>
            <input v-model="formData.cnpj" type="text" placeholder="00.000.000/0000-00"
              class="sky-input" @input="markDirty" />
          </div>
          <div>
            <label class="block text-xs text-n-slate-9 mb-0.5">Ramo de Atuação</label>
            <input v-model="formData.ramo_atuacao" type="text" placeholder="Ramo"
              class="sky-input" @input="markDirty" />
          </div>
          <div>
            <label class="block text-xs text-n-slate-9 mb-0.5">Setor</label>
            <select v-model="formData.setor" class="sky-input" @change="markDirty">
              <option value="">— Selecione —</option>
              <option v-for="s in ['Comercial','Financeiro','TI','RH','Marketing','Operações','Logística','Jurídico','Saúde','Educação','Outros']" :key="s" :value="s">{{ s }}</option>
            </select>
          </div>
          <div>
            <label class="block text-xs text-n-slate-9 mb-0.5">Segmento</label>
            <select v-model="formData.segmento" class="sky-input" @change="markDirty">
              <option value="">— Selecione —</option>
              <option v-for="s in ['B2B','B2C','B2G','Varejo','Serviços','Indústria','Tecnologia','Saúde','Educação','Outros']" :key="s" :value="s">{{ s }}</option>
            </select>
          </div>
          <div class="col-span-2">
            <label class="block text-xs text-n-slate-9 mb-0.5">Logo da Empresa (URL)</label>
            <div class="flex gap-1">
              <input v-model="formData.logo_empresa" type="url" placeholder="https://..."
                class="sky-input flex-1" @input="markDirty" />
              <img v-if="formData.logo_empresa" :src="formData.logo_empresa"
                class="h-7 w-7 rounded object-contain border border-n-weak" />
            </div>
          </div>
        </div>
      </div>

      <!-- Seção: Contatos & Links -->
      <div class="px-3 py-2 border-b border-n-weak">
        <p class="text-xs font-semibold text-n-slate-8 uppercase tracking-wider mb-2">Contatos & Links</p>
        <div class="grid grid-cols-2 gap-2">
          <div>
            <label class="block text-xs text-n-slate-9 mb-0.5">Telefone do Cliente</label>
            <input v-model="formData.telefone_cliente" type="tel" placeholder="+55 11 99999-9999"
              class="sky-input" @input="markDirty" />
          </div>
          <div>
            <label class="block text-xs text-n-slate-9 mb-0.5">Telefone do Agente</label>
            <input v-model="formData.telefone_agente" type="tel" placeholder="+55 11 99999-9999"
              class="sky-input" @input="markDirty" />
          </div>
          <div>
            <label class="block text-xs text-n-slate-9 mb-0.5">Telefone de Cobrança</label>
            <input v-model="formData.telefone_cobranca" type="tel" placeholder="+55 11 99999-9999"
              class="sky-input" @input="markDirty" />
          </div>
          <div>
            <label class="block text-xs text-n-slate-9 mb-0.5">Tipo de Agente</label>
            <select v-model="formData.tipo_agente" class="sky-input" @change="markDirty">
              <option value="">— Selecione —</option>
              <option v-for="t in ['Interno','Externo','Parceiro','Consultor','SDR','Closer','CS']" :key="t" :value="t">{{ t }}</option>
            </select>
          </div>
          <div class="col-span-2">
            <label class="block text-xs text-n-slate-9 mb-0.5">Link Grupo WhatsApp</label>
            <input v-model="formData.link_grupo" type="url" placeholder="https://chat.whatsapp.com/..."
              class="sky-input" @input="markDirty" />
          </div>
        </div>
      </div>

      <!-- Seção: Localização -->
      <div class="px-3 py-2 border-b border-n-weak">
        <p class="text-xs font-semibold text-n-slate-8 uppercase tracking-wider mb-2">Localização</p>
        <div class="grid grid-cols-2 gap-2">
          <div>
            <label class="block text-xs text-n-slate-9 mb-0.5">Estado (UF)</label>
            <select v-model="formData.estado" class="sky-input" @change="markDirty">
              <option value="">— UF —</option>
              <option v-for="uf in BRAZIL_STATES" :key="uf" :value="uf">{{ uf }}</option>
            </select>
          </div>
          <div>
            <label class="block text-xs text-n-slate-9 mb-0.5">Cidade</label>
            <select
              v-if="cities.length"
              v-model="formData.cidade"
              class="sky-input"
              @change="markDirty"
            >
              <option value="">— Selecione —</option>
              <option v-for="c in cities" :key="c" :value="c">{{ c }}</option>
            </select>
            <input
              v-else
              v-model="formData.cidade"
              type="text"
              :placeholder="loadingCities ? 'Carregando...' : 'Cidade'"
              class="sky-input"
              @input="markDirty"
            />
          </div>
          <div class="col-span-2">
            <label class="block text-xs text-n-slate-9 mb-0.5">Endereço</label>
            <input v-model="formData.endereco" type="text" placeholder="Rua, número, bairro"
              class="sky-input" @input="markDirty" />
          </div>
        </div>
      </div>

      <!-- Seção: Contrato -->
      <div class="px-3 py-2 border-b border-n-weak">
        <p class="text-xs font-semibold text-n-slate-8 uppercase tracking-wider mb-2">Contrato</p>
        <div class="grid grid-cols-2 gap-2">
          <div>
            <label class="block text-xs text-n-slate-9 mb-0.5">Início do Contrato</label>
            <input v-model="formData.data_inicio_contrato" type="date"
              class="sky-input" @change="markDirty" />
          </div>
          <div>
            <label class="block text-xs text-n-slate-9 mb-0.5">Encerramento</label>
            <input v-model="formData.encerramento_contrato" type="date"
              class="sky-input" @change="markDirty" />
          </div>
        </div>
      </div>

      <!-- Seção: Financeiro -->
      <div class="px-3 py-2 border-b border-n-weak">
        <p class="text-xs font-semibold text-n-slate-8 uppercase tracking-wider mb-2">Financeiro</p>
        <div class="grid grid-cols-2 gap-2">
          <div>
            <label class="block text-xs text-n-slate-9 mb-0.5">Implementação (R$)</label>
            <input v-model="formData.valor_implementacao" type="number" min="0" placeholder="0,00"
              class="sky-input" @input="markDirty" />
          </div>
          <div>
            <label class="block text-xs text-n-slate-9 mb-0.5">Mensalidade (R$)</label>
            <input v-model="formData.valor_mensalidade" type="number" min="0" placeholder="0,00"
              class="sky-input" @input="markDirty" />
          </div>
          <div>
            <label class="block text-xs text-n-slate-9 mb-0.5">Data Mensalidade</label>
            <input v-model="formData.data_mensalidade" type="date"
              class="sky-input" @change="markDirty" />
          </div>
          <div>
            <label class="block text-xs text-n-slate-9 mb-0.5">Valor Total (impl + mens×12)</label>
            <div class="sky-input bg-n-slate-1 dark:bg-n-surface-2 text-n-brand font-semibold cursor-default select-none">
              {{ totalValueFormatted }}
            </div>
          </div>
        </div>
      </div>

      <!-- Seção: Status & Classificação -->
      <div class="px-3 py-2 border-b border-n-weak">
        <p class="text-xs font-semibold text-n-slate-8 uppercase tracking-wider mb-2">Status & Classificação</p>
        <div class="grid grid-cols-2 gap-2">
          <div class="col-span-2">
            <label class="block text-xs text-n-slate-9 mb-0.5">Status</label>
            <select v-model="formData.sky_status" class="sky-input" @change="markDirty">
              <option value="">— Selecione —</option>
              <option v-for="s in STATUS_OPTIONS" :key="s" :value="s">{{ s }}</option>
            </select>
          </div>
          <div>
            <label class="block text-xs text-n-slate-9 mb-0.5">Termômetro</label>
            <div class="flex gap-1">
              <button
                v-for="opt in ['frio','morno','quente']"
                :key="opt"
                class="flex-1 py-1.5 text-xs rounded-lg border transition-colors font-medium"
                :class="formData.termometro === opt
                  ? opt === 'frio' ? 'border-blue-400 bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
                  : opt === 'morno' ? 'border-amber-400 bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400'
                  : 'border-red-400 bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-400'
                  : 'border-n-weak text-n-slate-9 hover:bg-n-slate-1'"
                @click="formData.termometro = opt; markDirty()"
              >{{ opt }}</button>
            </div>
          </div>
          <div>
            <label class="block text-xs text-n-slate-9 mb-0.5">Origem</label>
            <input v-model="formData.origem" type="text" placeholder="Ex: indicação, ads..."
              class="sky-input" @input="markDirty" />
          </div>
        </div>
      </div>

      <!-- Seção: Observações -->
      <div class="px-3 py-2 border-b border-n-weak">
        <p class="text-xs font-semibold text-n-slate-8 uppercase tracking-wider mb-2">Observações</p>
        <div class="flex flex-col gap-2">
          <div>
            <label class="block text-xs text-n-slate-9 mb-0.5">Observações</label>
            <textarea v-model="formData.sky_description" rows="3" placeholder="Anotações gerais..."
              class="sky-input resize-none" @input="markDirty" />
          </div>
          <div>
            <label class="block text-xs text-n-slate-9 mb-0.5">Histórico</label>
            <textarea v-model="formData.historico" rows="3" placeholder="Histórico do cliente..."
              class="sky-input resize-none" @input="markDirty" />
          </div>
        </div>
      </div>
    </div>

    <!-- ── ABA PROJETO ──────────────────────────────────────────────────────── -->
    <div v-else-if="activeTab === 'projeto'" class="flex flex-col gap-0 text-sm">

      <!-- Visão Geral -->
      <div class="px-3 py-2 border-b border-n-weak">
        <p class="text-xs font-semibold text-n-slate-8 uppercase tracking-wider mb-2">Visão Geral</p>
        <div class="flex flex-col gap-2">
          <div>
            <label class="block text-xs text-n-slate-9 mb-0.5">Entregáveis</label>
            <textarea
              v-model="formData.entregaveis"
              rows="3"
              placeholder="Descreva os entregáveis discutidos na reunião diagnóstico..."
              class="sky-input resize-none"
              @input="markDirty"
            />
          </div>
          <div>
            <label class="block text-xs text-n-slate-9 mb-0.5">Resumo Reunião / Objetivos</label>
            <textarea
              v-model="formData.resumo_reuniao"
              rows="3"
              placeholder="Resumo da reunião e objetivos do projeto..."
              class="sky-input resize-none"
              @input="markDirty"
            />
          </div>
        </div>
      </div>

      <!-- Entregáveis Contratados (lista dinâmica) -->
      <div class="border-b border-n-weak">
        <button
          class="w-full flex items-center justify-between px-3 py-2 hover:bg-n-slate-1 transition-colors"
          @click="openSections.entregaveis = !openSections.entregaveis"
        >
          <span class="text-xs font-semibold text-n-slate-10">Entregáveis Contratados</span>
          <span :class="openSections.entregaveis ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'" class="size-3.5 text-n-slate-8" />
        </button>
        <div v-if="openSections.entregaveis" class="px-3 pb-3 flex flex-col gap-2">
          <div
            v-for="(item, idx) in (skyData.entregaveis_contratados || [])"
            :key="item.id || idx"
            class="rounded-lg border border-n-weak p-2 bg-n-slate-1 dark:bg-n-surface-2 flex flex-col gap-1.5"
          >
            <div class="flex items-center gap-1">
              <input
                v-model="item.nome"
                type="text"
                placeholder="Nome do entregável"
                class="sky-input flex-1 text-xs font-medium"
                @input="markDirty"
              />
              <button class="p-1 rounded hover:bg-red-50 text-red-400 hover:text-red-600 dark:hover:bg-red-900/20 transition-colors"
                @click="removeItem('entregaveis_contratados', idx)">
                <span class="i-lucide-x size-3.5" />
              </button>
            </div>
            <textarea
              v-model="item.descricao"
              rows="2"
              placeholder="Descrição..."
              class="sky-input resize-none text-xs"
              @input="markDirty"
            />
          </div>
          <button
            class="flex items-center gap-1 text-xs text-n-brand hover:underline"
            @click="addItem('entregaveis_contratados', { nome: '', descricao: '' })"
          >
            <span class="i-lucide-plus size-3.5" />
            Adicionar entregável
          </button>
        </div>
      </div>

      <!-- Integrações (lista dinâmica) -->
      <div class="border-b border-n-weak">
        <button
          class="w-full flex items-center justify-between px-3 py-2 hover:bg-n-slate-1 transition-colors"
          @click="openSections.integracoes = !openSections.integracoes"
        >
          <span class="text-xs font-semibold text-n-slate-10">Integrações</span>
          <span :class="openSections.integracoes ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'" class="size-3.5 text-n-slate-8" />
        </button>
        <div v-if="openSections.integracoes" class="px-3 pb-3 flex flex-col gap-2">
          <div
            v-for="(item, idx) in (skyData.integracoes || [])"
            :key="item.id || idx"
            class="rounded-lg border border-n-weak p-2 bg-n-slate-1 dark:bg-n-surface-2 flex flex-col gap-1.5"
          >
            <div class="flex items-center gap-1">
              <input
                v-model="item.nome_sistema"
                type="text"
                placeholder="Nome do sistema"
                class="sky-input flex-1 text-xs font-medium"
                @input="markDirty"
              />
              <button class="p-1 rounded hover:bg-red-50 text-red-400 hover:text-red-600 dark:hover:bg-red-900/20 transition-colors"
                @click="removeItem('integracoes', idx)">
                <span class="i-lucide-x size-3.5" />
              </button>
            </div>
            <textarea
              v-model="item.integracoes_fazer"
              rows="2"
              placeholder="Integrações a serem feitas..."
              class="sky-input resize-none text-xs"
              @input="markDirty"
            />
            <input
              v-model="item.documentacao_api"
              type="url"
              placeholder="URL da documentação API"
              class="sky-input text-xs"
              @input="markDirty"
            />
          </div>
          <button
            class="flex items-center gap-1 text-xs text-n-brand hover:underline"
            @click="addItem('integracoes', { nome_sistema: '', integracoes_fazer: '', documentacao_api: '' })"
          >
            <span class="i-lucide-plus size-3.5" />
            Adicionar integração
          </button>
        </div>
      </div>

      <!-- Negociação (colapsível) -->
      <div class="border-b border-n-weak">
        <button
          class="w-full flex items-center justify-between px-3 py-2 hover:bg-n-slate-1 transition-colors"
          @click="openSections.negociacao = !openSections.negociacao"
        >
          <span class="text-xs font-semibold text-n-slate-10">Negociação</span>
          <span :class="openSections.negociacao ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'" class="size-3.5 text-n-slate-8" />
        </button>
        <div v-if="openSections.negociacao" class="px-3 pb-3 flex flex-col gap-2">
          <div>
            <label class="block text-xs text-n-slate-9 mb-0.5">Negociação — Implementação</label>
            <div class="relative">
              <textarea
                v-model="formData.negociacao_implementacao"
                rows="6"
                class="sky-input resize-none text-xs"
                @input="markDirty"
              />
              <button
                v-if="!formData.negociacao_implementacao"
                class="absolute top-1.5 right-1.5 text-xs text-n-brand hover:underline"
                @click="initNegTemplate('negociacao_implementacao', NEG_IMPL_TEMPLATE)"
              >Template</button>
            </div>
          </div>
          <div>
            <label class="block text-xs text-n-slate-9 mb-0.5">Negociação — Mensalidade</label>
            <div class="relative">
              <textarea
                v-model="formData.negociacao_mensalidade"
                rows="6"
                class="sky-input resize-none text-xs"
                @input="markDirty"
              />
              <button
                v-if="!formData.negociacao_mensalidade"
                class="absolute top-1.5 right-1.5 text-xs text-n-brand hover:underline"
                @click="initNegTemplate('negociacao_mensalidade', NEG_MENS_TEMPLATE)"
              >Template</button>
            </div>
          </div>
          <div>
            <label class="block text-xs text-n-slate-9 mb-0.5">Informações Adicionais para Contrato</label>
            <textarea
              v-model="formData.informacoes_adicionais_contrato"
              rows="3"
              placeholder="Informações adicionais..."
              class="sky-input resize-none text-xs"
              @input="markDirty"
            />
          </div>
        </div>
      </div>

      <!-- Documentos (lista dinâmica, colapsível) -->
      <div class="border-b border-n-weak">
        <button
          class="w-full flex items-center justify-between px-3 py-2 hover:bg-n-slate-1 transition-colors"
          @click="openSections.documentos = !openSections.documentos"
        >
          <span class="text-xs font-semibold text-n-slate-10">Documentos</span>
          <span :class="openSections.documentos ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'" class="size-3.5 text-n-slate-8" />
        </button>
        <div v-if="openSections.documentos" class="px-3 pb-3 flex flex-col gap-2">
          <div
            v-for="(item, idx) in (skyData.documentos || [])"
            :key="item.id || idx"
            class="rounded-lg border border-n-weak p-2 bg-n-slate-1 dark:bg-n-surface-2 flex flex-col gap-1.5"
          >
            <div class="flex items-center gap-1">
              <input
                v-model="item.nome"
                type="text"
                placeholder="Nome do documento"
                class="sky-input flex-1 text-xs font-medium"
                @input="markDirty"
              />
              <button class="p-1 rounded hover:bg-red-50 text-red-400 hover:text-red-600 dark:hover:bg-red-900/20 transition-colors"
                @click="removeItem('documentos', idx)">
                <span class="i-lucide-x size-3.5" />
              </button>
            </div>
            <input
              v-model="item.url"
              type="url"
              placeholder="URL do documento"
              class="sky-input text-xs"
              @input="markDirty"
            />
          </div>
          <button
            class="flex items-center gap-1 text-xs text-n-brand hover:underline"
            @click="addItem('documentos', { nome: '', url: '' })"
          >
            <span class="i-lucide-plus size-3.5" />
            Adicionar documento
          </button>
        </div>
      </div>

      <!-- Links Adicionais (lista dinâmica, colapsível) -->
      <div class="border-b border-n-weak">
        <button
          class="w-full flex items-center justify-between px-3 py-2 hover:bg-n-slate-1 transition-colors"
          @click="openSections.link_adicionais = !openSections.link_adicionais"
        >
          <span class="text-xs font-semibold text-n-slate-10">Links Adicionais</span>
          <span :class="openSections.link_adicionais ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'" class="size-3.5 text-n-slate-8" />
        </button>
        <div v-if="openSections.link_adicionais" class="px-3 pb-3 flex flex-col gap-2">
          <div
            v-for="(item, idx) in (skyData.link_adicionais || [])"
            :key="item.id || idx"
            class="rounded-lg border border-n-weak p-2 bg-n-slate-1 dark:bg-n-surface-2 flex flex-col gap-1.5"
          >
            <div class="flex items-center gap-1">
              <input
                v-model="item.nome"
                type="text"
                placeholder="Nome do link"
                class="sky-input flex-1 text-xs font-medium"
                @input="markDirty"
              />
              <button class="p-1 rounded hover:bg-red-50 text-red-400 hover:text-red-600 dark:hover:bg-red-900/20 transition-colors"
                @click="removeItem('link_adicionais', idx)">
                <span class="i-lucide-x size-3.5" />
              </button>
            </div>
            <input
              v-model="item.url"
              type="url"
              placeholder="URL"
              class="sky-input text-xs"
              @input="markDirty"
            />
          </div>
          <button
            class="flex items-center gap-1 text-xs text-n-brand hover:underline"
            @click="addItem('link_adicionais', { nome: '', url: '' })"
          >
            <span class="i-lucide-plus size-3.5" />
            Adicionar link
          </button>
        </div>
      </div>

      <!-- Transcrições (lista dinâmica, colapsível) -->
      <div class="border-b border-n-weak">
        <button
          class="w-full flex items-center justify-between px-3 py-2 hover:bg-n-slate-1 transition-colors"
          @click="openSections.transcricoes = !openSections.transcricoes"
        >
          <span class="text-xs font-semibold text-n-slate-10">Transcrições (.md)</span>
          <span :class="openSections.transcricoes ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'" class="size-3.5 text-n-slate-8" />
        </button>
        <div v-if="openSections.transcricoes" class="px-3 pb-3 flex flex-col gap-2">
          <div
            v-for="(item, idx) in (skyData.transcricoes_markdown || [])"
            :key="item.id || idx"
            class="rounded-lg border border-n-weak p-2 bg-n-slate-1 dark:bg-n-surface-2 flex flex-col gap-1.5"
          >
            <div class="flex items-center gap-1">
              <input
                v-model="item.titulo"
                type="text"
                placeholder="Título"
                class="sky-input flex-1 text-xs font-medium"
                @input="markDirty"
              />
              <input
                v-model="item.nome_arquivo"
                type="text"
                placeholder="Nome do arquivo"
                class="sky-input w-28 text-xs"
                @input="markDirty"
              />
              <button class="p-1 rounded hover:bg-red-50 text-red-400 hover:text-red-600 dark:hover:bg-red-900/20 transition-colors"
                @click="removeItem('transcricoes_markdown', idx)">
                <span class="i-lucide-x size-3.5" />
              </button>
            </div>
            <textarea
              v-model="item.conteudo"
              rows="4"
              placeholder="Conteúdo da transcrição (markdown)..."
              class="sky-input resize-y text-xs font-mono"
              @input="markDirty"
            />
          </div>
          <button
            class="flex items-center gap-1 text-xs text-n-brand hover:underline"
            @click="addItem('transcricoes_markdown', { titulo: '', nome_arquivo: '', conteudo: '' })"
          >
            <span class="i-lucide-plus size-3.5" />
            Adicionar transcrição
          </button>
        </div>
      </div>
    </div>

    <!-- ── Botão Salvar ──────────────────────────────────────────────────────── -->
    <div
      v-if="isDirty"
      class="sticky bottom-0 px-3 py-2 bg-n-background border-t border-n-weak flex items-center gap-2 shadow-sm"
    >
      <button
        class="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold text-white transition-colors"
        :class="isSaving ? 'bg-n-brand/60 cursor-not-allowed' : 'bg-n-brand hover:bg-n-brand/90'"
        :disabled="isSaving"
        @click="save"
      >
        <span :class="isSaving ? 'i-lucide-loader-2 animate-spin' : 'i-lucide-save'" class="size-3.5" />
        {{ isSaving ? 'Salvando...' : 'Salvar Alterações' }}
      </button>
      <button
        class="px-3 py-2 rounded-lg text-xs text-n-slate-9 hover:bg-n-slate-2 transition-colors"
        @click="syncFromContact(); isDirty = false"
      >
        Cancelar
      </button>
    </div>
  </div>
</template>

<style>
.sky-input {
  @apply w-full px-2 py-1.5 text-sm bg-n-background border border-n-weak rounded-lg
         focus:outline-none focus:ring-1 focus:ring-n-brand
         text-n-slate-12 placeholder:text-n-slate-7
         dark:bg-n-surface-1 transition-colors;
}
</style>
